import React from "react";
import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from '@/integrations/supabase/client';
import OrientationEstimationButton from "./OrientationEstimationButton";

interface TerraceBasicInfoFormProps {
  form: UseFormReturn<any>;
  terraceId?: string;
}

const orientationOptions = [
  { value: "unknown", label: "Unknown" },
  { value: "N", label: "North (N) - 0°" },
  { value: "NE", label: "Northeast (NE) - 45°" },
  { value: "E", label: "East (E) - 90°" },
  { value: "SE", label: "Southeast (SE) - 135°" },
  { value: "S", label: "South (S) - 180°" },
  { value: "SW", label: "Southwest (SW) - 225°" },
  { value: "W", label: "West (W) - 270°" },
  { value: "NW", label: "Northwest (NW) - 315°" },
];

const TerraceBasicInfoForm = ({ form, terraceId }: TerraceBasicInfoFormProps) => {
  const googleMapsUrl = form.watch("googleMapsUrl");

  const handleEstimationComplete = async () => {
    if (!terraceId) return;
    
    try {
      // Fetch the updated terrace data from the database
      const { data: updatedTerrace, error } = await supabase
        .from('terraces')
        .select('orientation')
        .eq('id', terraceId)
        .single();

      if (error) {
        console.error('Error fetching updated terrace:', error);
        return;
      }

      if (updatedTerrace?.orientation) {
        // Update the form field with the new orientation value
        form.setValue('orientation', updatedTerrace.orientation);
        console.log('Updated form orientation to:', updatedTerrace.orientation);
      }
    } catch (error) {
      console.error('Error updating form after estimation:', error);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="cafe">Cafe</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="hotel">Hotel</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Show Google Maps URL below Address if present */}
      {googleMapsUrl && googleMapsUrl.length > 5 && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Google Maps URL</label>
          <div className="flex items-end gap-2">
            <Input
              type="url"
              value={googleMapsUrl}
              readOnly
              tabIndex={-1}
              className="text-xs font-mono bg-muted w-full cursor-not-allowed"
            />
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in Google Maps"
              className="mb-1"
            >
              {/* External link icon from lucide-react if desired */}
              <span className="inline-block px-1 text-blue-500 hover:underline text-xs">Open</span>
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://..." />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="orientation"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center justify-between">
                Orientation
                {terraceId && (
                  <OrientationEstimationButton 
                    terraceId={terraceId}
                    onEstimationComplete={handleEstimationComplete}
                    size="sm"
                  />
                )}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || "unknown"}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select orientation" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border border-border shadow-lg z-50 max-h-60 overflow-y-auto">
                  {orientationOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      className="bg-white hover:bg-gray-50 cursor-pointer"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="sunStatus"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sun Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="sunny">Sunny</SelectItem>
                  <SelectItem value="soon">Soon</SelectItem>
                  <SelectItem value="not-sunny">Not Sunny</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="terraceDescription"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default TerraceBasicInfoForm;
